/**
 * Locations + Departments admin API module.
 *
 * Extracted from the AdminService god class in services/auth.ts. Covers
 * locations (CRUD, search, counts, metrics, dashboard, logo), department
 * sections, departments (CRUD, reorder, service linking), and location
 * workflows. All requests route through the central web ApiClient (CSRF,
 * traceparent, 401 auto-logout, observability) instead of raw fetch().
 *
 * Method names, signatures, and behavior intentionally mirror the legacy
 * AdminService methods one-for-one so a later step can compose them back
 * into the `adminService` export.
 */

import { webApiClient, unwrap } from '../apiClient';
import { dedup, extractItems } from './helpers';
import type { Location, LocationDashboardResponse } from '@turn-connect/shared';
import { refreshLocationNames } from '../../queries/nameCaches';

// ---------------------------------------------------------------------------
// Locations
// ---------------------------------------------------------------------------

// Location/Location Logo helpers (prefer new locations path)
export function getLocationLogoUrl(locationId: string): string {
  return `${webApiClient.getBaseUrl()}/locations/${locationId}/logo`;
}

export async function uploadLocationLogo(locationId: string, file: Blob): Promise<void> {
  // Locations only (no legacy fallback)
  const form = new FormData();
  form.append('logo', file);
  await unwrap(webApiClient.uploadMultipart<any>(`/locations/${locationId}/logo`, form), 'Failed to upload location logo');
}

/**
 * Fetches EVERY page of `/accounts/{id}/locations`.
 *
 * The backend defaults `limit` to 20 and clamps it to 100, so any caller that
 * omits the parameter silently receives the first 20 locations ordered by name
 * and no indication that more exist. On an account with 69 active locations
 * that hid 49 of them — the location selector showed nothing past "H", and
 * operators reported real shops simply missing.
 *
 * This helper exists so that failure cannot recur one caller at a time. There
 * used to be two functions here: one looped correctly and one did not, and the
 * one that did not was the one feeding the selector. Anything that needs the
 * full list must go through this.
 */
async function fetchAllLocationPages(
  accountId: string,
  includeDeleted: boolean
): Promise<{ locations: Location[]; activeJobCounts: Record<string, number> }> {
  // 100 is the server's hard clamp (SanitizeLimits(..., 20, 100)); asking for
  // more is silently reduced, so this is the largest useful page.
  const PAGE_SIZE = 100;
  const locations: Location[] = [];
  const activeJobCounts: Record<string, number> = {};
  let offset = 0;
  // Defensive ceiling — even an unrealistically large account is bounded so a
  // runaway backend can't loop forever.
  const MAX_PAGES = 50;

  for (let page = 0; page < MAX_PAGES; page++) {
    const params = new URLSearchParams();
    if (includeDeleted) params.set('include_deleted', 'true');
    params.set('limit', String(PAGE_SIZE));
    params.set('offset', String(offset));
    const json = await unwrap<any>(webApiClient.get(`/accounts/${accountId}/locations?${params.toString()}`), 'Failed to fetch locations');
    const list = extractItems(json, ['locations']) as Location[];
    locations.push(...list);
    // Counts are keyed by location id, so later pages extend rather than
    // replace — merging is what makes the counts cover the full set too.
    Object.assign(activeJobCounts, json?.active_job_counts || {});

    // Use the server's has_more signal when present; fall back to
    // "last page returned a partial fill" for older response shapes
    // (some endpoints don't include has_more).
    const serverSaysMore: boolean | undefined =
      typeof json?.has_more === 'boolean' ? json.has_more : typeof json?.hasMore === 'boolean' ? json.hasMore : undefined;
    const hasMore = serverSaysMore !== undefined ? serverSaysMore : list.length >= PAGE_SIZE;
    if (!hasMore || list.length === 0) break;
    offset += list.length;
  }

  return { locations, activeJobCounts };
}

// Location Management - Get locations through accounts endpoint (fallback to locations)
export async function getLocations(accountId: string, includeDeleted: boolean = false): Promise<Location[]> {
  return dedup(`getLocations:${accountId}:${includeDeleted}`, async () => {
    const { locations } = await fetchAllLocationPages(accountId, includeDeleted);
    return locations;
  });
}

/**
 * Returns locations along with a map of location_id → active job count.
 * The counts respect the current user's department access restrictions.
 */
export type AccountLocationStatus = 'active' | 'deleted' | 'all';

export interface AccountLocationsPageParams {
  /** Words that must each match the name, code, city, state, street or group name. */
  search?: string;
  status?: AccountLocationStatus;
  /** A location group id, or 'none' for locations in no group. */
  groupId?: string;
  limit: number;
  offset: number;
}

export interface AccountLocationsPage {
  items: Location[];
  /** Every location matching the filters, not just this page. */
  total: number;
}

/**
 * One page of an account's locations, filtered, counted and sorted by name on
 * the server — for lists that page instead of loading every location.
 */
export async function getAccountLocationsPage(accountId: string, p: AccountLocationsPageParams): Promise<AccountLocationsPage> {
  const params = new URLSearchParams();
  if (p.search?.trim()) params.set('search', p.search.trim());
  if (p.status && p.status !== 'active') params.set('status', p.status);
  if (p.groupId) params.set('location_group_id', p.groupId);
  params.set('limit', String(p.limit));
  params.set('offset', String(p.offset));
  const json = await unwrap<any>(webApiClient.get(`/accounts/${accountId}/locations?${params.toString()}`), 'Failed to fetch locations');
  const items = extractItems(json, ['locations']) as Location[];
  return { items, total: typeof json?.total === 'number' ? json.total : items.length };
}

/** A location as the names endpoint returns it: enough for a picker or a name lookup. */
export interface LocationName {
  id: string;
  name: string;
  deleted_at?: string | null;
}

/**
 * Every location of the account the caller may see, as id and name, in one
 * request ordered by name — for pickers, filters and name lookups. Walking
 * /accounts/{id}/locations a hundred full rows at a time to read two fields
 * of each was how the settings panels loaded this before.
 */
export async function getAccountLocationNames(accountId: string, opts: { includeDeleted?: boolean } = {}): Promise<LocationName[]> {
  const qs = opts.includeDeleted ? '?include_deleted=true' : '';
  return dedup(`getAccountLocationNames:${accountId}:${!!opts.includeDeleted}`, async () => {
    const json = await unwrap<any>(webApiClient.get(`/accounts/${accountId}/locations/names${qs}`), 'Failed to fetch location names');
    return extractItems(json, ['locations']) as LocationName[];
  });
}

export async function getLocationsWithCounts(
  accountId: string,
  includeDeleted: boolean = false
): Promise<{ locations: Location[]; activeJobCounts: Record<string, number> }> {
  return dedup(`getLocationsWithCounts:${accountId}:${includeDeleted}`, async () => {
    // Was a single unparameterised request, so it took the backend's default
    // limit of 20. This is the call behind the location selector.
    return fetchAllLocationPages(accountId, includeDeleted);
  });
}

export interface LocationSearchOptions {
  account_id?: string;
  limit?: number;
  offset?: number;
  include_deleted?: boolean;
}

export interface LocationSearchPage {
  items: any[];
  /** Full number of matches, which may exceed the page returned. */
  total: number;
}

/**
 * Search locations, with the server's full match count.
 *
 * The backend matches name, external id, and the city/street of the address,
 * across every account the caller belongs to (system admins: all accounts, or
 * one via account_id). `total` is the whole match count, not the page size, so
 * a filtered view can say "showing 50 of 340" rather than implying it has
 * everything.
 */
export async function searchLocationsPage(query: string, opts: LocationSearchOptions = {}): Promise<LocationSearchPage> {
  const params = new URLSearchParams();
  if (query) params.set('q', query);
  if (opts.account_id) params.set('account_id', String(opts.account_id));
  if (opts.limit != null) params.set('limit', String(opts.limit));
  if (opts.offset != null) params.set('offset', String(opts.offset));
  if (opts.include_deleted) params.set('include_deleted', 'true');
  const json = await unwrap<any>(
    webApiClient.get(`/locations/search${params.toString() ? `?${params.toString()}` : ''}`),
    'Failed to search locations'
  );
  const items = extractItems(json, ['locations']);
  return { items, total: typeof json?.total === 'number' ? json.total : items.length };
}

// Search locations globally, optionally filtered by account. Kept for callers
// that only want the rows; searchLocationsPage adds the match count.
export async function searchLocations(query: string, opts: LocationSearchOptions = {}): Promise<any[]> {
  return (await searchLocationsPage(query, opts)).items;
}

export async function getLocation(locationId: string): Promise<Location> {
  return dedup(`getLocation:${locationId}`, async () => {
    return unwrap<Location>(webApiClient.get(`/locations/${locationId}`), 'Failed to fetch location');
  });
}

// Location dashboard (aggregated sections and metrics)
export async function getLocationDashboard(locationId: string, opts?: { tag_ids?: string | string[] }): Promise<LocationDashboardResponse> {
  const params = new URLSearchParams();
  if (opts?.tag_ids) {
    const tagValue = Array.isArray(opts.tag_ids) ? opts.tag_ids.filter(Boolean).join(',') : opts.tag_ids;
    if (tagValue) params.set('tag_ids', tagValue);
  }
  const qs = params.toString();
  return unwrap<LocationDashboardResponse>(
    webApiClient.get(`/locations/${locationId}/dashboard${qs ? `?${qs}` : ''}`),
    'Failed to fetch location dashboard'
  );
}

export async function createLocation(location: Partial<Location> & { size?: string }, accountId?: string): Promise<Location> {
  // If no accountId provided, try to get it from current user context
  if (!accountId) {
    const userContext = await unwrap<any>(webApiClient.get('/auth/me'), 'Failed to fetch user context');
    if (userContext && userContext.account_id) {
      accountId = userContext.account_id;
    } else if (userContext && userContext.accounts && userContext.accounts.length > 0) {
      accountId = userContext.accounts[0].id;
    } else {
      throw new Error('No account ID available for location creation');
    }
  }

  // Per OpenAPI, create is POST /locations with body { account_id, name, size, ... }
  const payload: any = {
    account_id: accountId,
    name: location.name,
    size: location.size || 'small',
    description: location.description,
    email: (location as any).email,
    industry: (location as any).industry,
    phone_number: (location as any).phone_number,
    registration_number: (location as any).registration_number,
    website: (location as any).website,
    address: (location as any).address,
    // Production-site flag. Backend defaults true; only forward an explicit
    // boolean so a missing value preserves the default. Must be listed here
    // because createLocation builds an explicit allowlist payload (it does
    // NOT spread `location`), so any key omitted here is silently dropped.
    production_site: typeof (location as any).production_site === 'boolean' ? (location as any).production_site : undefined,
    // Location code (work-order prefix) and time zone. Left out, the API
    // generates a code from the name and uses the default time zone, so what
    // the person typed was silently replaced.
    work_order_prefix: (location as any).work_order_prefix || undefined,
    timezone: (location as any).timezone || undefined,
    // Optional starting values; left out, the location follows the account.
    labor_rate: (location as any).labor_rate,
    auto_approval_amount: (location as any).auto_approval_amount,
  };

  const json = await unwrap<any>(webApiClient.post('/locations', payload), 'Failed to create location');
  refreshLocationNames();
  return (json?.data ?? json) as Location;
}

// Location update/delete (per swagger: PUT/DELETE /locations/{id})
export async function updateLocation(locationId: string, location: Partial<Location>): Promise<Location> {
  const updated = await unwrap<Location>(webApiClient.put(`/locations/${locationId}`, location), 'Failed to update location');
  refreshLocationNames();
  return updated;
}

export async function deleteLocation(locationId: string): Promise<void> {
  await unwrap(webApiClient.delete(`/locations/${locationId}`), 'Failed to delete location');
  refreshLocationNames();
}

export async function undeleteLocation(locationId: string): Promise<void> {
  await unwrap(webApiClient.post(`/locations/${locationId}/restore`), 'Failed to restore location');
  refreshLocationNames();
}

// NOTE: exportLocations / importLocations / copyLocationStructure live in
// importExport.ts alongside the other CSV/Excel import-export methods.

// ---------------------------------------------------------------------------
// Department Sections
// ---------------------------------------------------------------------------

export async function listDepartmentSections(locationId: string, includeDeleted: boolean = false): Promise<Array<any>> {
  return dedup(`listDepartmentSections:${locationId}:${includeDeleted}`, async () => {
    const json = await unwrap<any>(
      webApiClient.get(`/locations/${locationId}/department-sections${includeDeleted ? '?include_deleted=true' : ''}`),
      'Failed to list department sections'
    );
    const items = Array.isArray(json) ? json : (json?.items ?? json?.data ?? json?.sections ?? []);
    // Return all fields from API, don't strip them
    return items;
  });
}

export async function createDepartmentSection(
  locationId: string,
  payload: { name: string; order?: number; icon?: string; color?: string }
): Promise<{ id: string; name: string; order?: number; icon?: string; color?: string }> {
  const j = await unwrap<any>(
    webApiClient.post(`/locations/${locationId}/department-sections`, payload),
    'Failed to create department section'
  );
  return { id: String(j.id), name: String(j.name), order: j.order != null ? Number(j.order) : undefined, icon: j.icon, color: j.color };
}

export async function updateDepartmentSection(
  sectionId: string,
  payload: { name?: string; order?: number; icon?: string; color?: string }
): Promise<{ id: string; name: string; order?: number; icon?: string; color?: string }> {
  const j = await unwrap<any>(webApiClient.put(`/department-sections/${sectionId}`, payload), 'Failed to update department section');
  return { id: String(j.id), name: String(j.name), order: j.order != null ? Number(j.order) : undefined, icon: j.icon, color: j.color };
}

export async function moveDepartmentSectionUp(sectionId: string): Promise<void> {
  await unwrap(webApiClient.post(`/department-sections/${sectionId}/move-up`), 'Failed to move section up');
}

export async function moveDepartmentSectionDown(sectionId: string): Promise<void> {
  await unwrap(webApiClient.post(`/department-sections/${sectionId}/move-down`), 'Failed to move section down');
}

export async function reorderDepartmentSection(sectionId: string, position: number): Promise<void> {
  // API expects 1-based order; accept 0-based position from UI and convert
  const order = Math.max(1, Math.floor(position) + 1);
  await unwrap(webApiClient.post(`/department-sections/${sectionId}/reorder`, { order }), 'Failed to reorder section');
}

export async function deleteDepartmentSection(sectionId: string): Promise<void> {
  await unwrap(webApiClient.delete(`/department-sections/${sectionId}`), 'Failed to delete department section');
}

export async function undeleteDepartmentSection(sectionId: string): Promise<void> {
  await unwrap(webApiClient.post(`/department-sections/${sectionId}/undelete`), 'Failed to restore department section');
}

export async function removeDepartmentFromSection(departmentId: string): Promise<void> {
  await unwrap(webApiClient.delete(`/departments/${departmentId}/section`), 'Failed to remove department from section');
}

// ---------------------------------------------------------------------------
// Departments
// ---------------------------------------------------------------------------

// Department Management
export async function getDepartments(locationId?: string, includeDeleted = false): Promise<any[]> {
  return dedup(`getDepartments:${locationId}:${includeDeleted}`, async () => {
    const endpoint = locationId
      ? `/locations/${locationId}/departments${includeDeleted ? '?include_deleted=true' : '?include_deleted=false'}`
      : `/departments${includeDeleted ? '?include_deleted=true' : '?include_deleted=false'}`;
    const res = await webApiClient.get<any>(endpoint);
    if (!res.success) {
      // Legacy behavior: constant message regardless of server body.
      throw new Error('Failed to fetch departments');
    }
    return extractItems(res.data, ['departments']);
  });
}

/**
 * Account-wide departments fetch — returns every department under the
 * account (account-scoped + every location's). Each item carries a
 * location_id (null for account-scoped departments). Used by the audit
 * report filter dropdowns to avoid the 29-location waterfall that
 * historically saturated nginx on MetroTech-sized accounts.
 *
 * Uses ?limit=100 because the existing /api/accounts/:id/departments
 * endpoint paginates and our largest account caps in the 80s today;
 * if any account grows past 100 we'll need to follow paging.
 */
export async function getAccountDepartments(accountId: string, includeDeleted = false): Promise<any[]> {
  return dedup(`getAccountDepartments:${accountId}:${includeDeleted}`, async () => {
    // The backend paginates `/accounts/{id}/departments` (max 500 per
    // page; older builds clamp to 50). A single `limit=100` fetch
    // silently dropped departments past row #100 — large accounts have
    // 100-450 departments across their locations, so the reports
    // department filter was missing most of them. Loop until
    // `has_more=false`, same as getLocations above.
    const PAGE_SIZE = 500;
    const all: any[] = [];
    let offset = 0;
    const MAX_PAGES = 50;
    for (let page = 0; page < MAX_PAGES; page++) {
      const params = new URLSearchParams();
      if (includeDeleted) params.set('include_deleted', 'true');
      params.set('limit', String(PAGE_SIZE));
      params.set('offset', String(offset));
      const json = await unwrap<any>(
        webApiClient.get(`/accounts/${accountId}/departments?${params.toString()}`),
        'Failed to fetch account departments'
      );
      const list = extractItems(json, ['items', 'departments']);
      all.push(...list);
      const serverSaysMore: boolean | undefined =
        typeof json?.has_more === 'boolean' ? json.has_more : typeof json?.hasMore === 'boolean' ? json.hasMore : undefined;
      // The server may clamp `limit` below PAGE_SIZE, so a partial fill
      // is not a reliable end signal on its own; trust has_more first.
      const hasMore = serverSaysMore !== undefined ? serverSaysMore : list.length >= PAGE_SIZE;
      if (!hasMore || list.length === 0) break;
      offset += list.length;
    }
    return all;
  });
}

// Get departments accessible to the current user (for self-scoped users)
export async function getMyDepartments(): Promise<any[]> {
  const json = await unwrap<any>(webApiClient.get('/departments/my'), 'Failed to fetch user departments');
  return extractItems(json, ['departments', 'items']);
}

export async function createDepartment(locationId: string, department: any): Promise<any> {
  return unwrap<any>(webApiClient.post(`/locations/${locationId}/departments`, department), 'Failed to create department');
}

export async function updateDepartment(departmentId: string, department: any): Promise<any> {
  return unwrap<any>(webApiClient.put(`/departments/${departmentId}`, department), 'Failed to update department');
}

// Note: Department section assignment is handled directly via update/create payloads (department_section_id)

// Department reordering
export async function batchReorderDepartments(
  locationId: string,
  departments: Array<{ id: string; display_order: number; department_section_id?: string }>
): Promise<void> {
  await unwrap(webApiClient.post(`/locations/${locationId}/departments/reorder`, { departments }), 'Failed to reorder departments');
}

export async function moveDepartmentUp(locationId: string, departmentId: string): Promise<void> {
  await unwrap(webApiClient.post(`/locations/${locationId}/departments/${departmentId}/move-up`), 'Failed to move department up');
}

export async function moveDepartmentDown(locationId: string, departmentId: string): Promise<void> {
  await unwrap(webApiClient.post(`/locations/${locationId}/departments/${departmentId}/move-down`), 'Failed to move department down');
}

export async function deleteDepartment(departmentId: string): Promise<void> {
  await unwrap(webApiClient.delete(`/departments/${departmentId}`), 'Failed to delete department');
}

export async function undeleteDepartment(departmentId: string): Promise<void> {
  await unwrap(webApiClient.post(`/departments/${departmentId}/undelete`), 'Failed to restore department');
}

// Department-service linking
export async function attachServiceToDepartment(departmentId: string, serviceId: string): Promise<void> {
  await unwrap(webApiClient.post(`/departments/${departmentId}/services/${serviceId}/attach`), 'Failed to attach service');
}

export async function detachServiceFromDepartment(departmentId: string, serviceId: string): Promise<void> {
  await unwrap(webApiClient.delete(`/departments/${departmentId}/services/${serviceId}`), 'Failed to detach service');
}

// ---------------------------------------------------------------------------
// Location Workflows
// ---------------------------------------------------------------------------

// Workflow Management
export async function getLocationWorkflows(locationId: string): Promise<any[]> {
  const json = await unwrap<any>(webApiClient.get(`/locations/${locationId}/workflows`), 'Failed to fetch workflows');
  return extractItems(json, ['workflows']);
}

export async function createWorkflow(locationId: string, workflow: { name: string; description?: string }): Promise<any> {
  return unwrap<any>(webApiClient.post(`/locations/${locationId}/workflows`, workflow), 'Failed to create workflow');
}

// Speculative endpoints for workflow update/delete (if supported by API)
export async function updateWorkflow(workflowId: string, data: { name?: string; description?: string }): Promise<any> {
  return unwrap<any>(webApiClient.put(`/workflows/${workflowId}`, data), 'Failed to update workflow');
}

export async function deleteWorkflow(workflowId: string): Promise<void> {
  await unwrap(webApiClient.delete(`/workflows/${workflowId}`), 'Failed to delete workflow');
}

export async function getWorkflowSteps(workflowId: string): Promise<any[]> {
  const res = await webApiClient.get<any>(`/workflows/${workflowId}/steps`);
  // Legacy behavior: constant message regardless of server body.
  if (!res.success) throw new Error('Failed to fetch workflow steps');
  return extractItems(res.data, ['steps']);
}

export async function addWorkflowStep(workflowId: string, step: { department_id: string; position?: number }): Promise<any> {
  return unwrap<any>(webApiClient.post(`/workflows/${workflowId}/steps`, step), 'Failed to add step');
}

export async function moveWorkflowStep(workflowId: string, stepId: string, direction: 'up' | 'down'): Promise<void> {
  // NOTE: underscore endpoints (move_up/move_down), unlike the hyphenated
  // department/section move endpoints.
  const endpoint = direction === 'up' ? 'move_up' : 'move_down';
  await unwrap(webApiClient.post(`/workflows/${workflowId}/steps/${stepId}/${endpoint}`), 'Failed to move step');
}

export async function deleteWorkflowStep(workflowId: string, stepId: string): Promise<void> {
  await unwrap(webApiClient.delete(`/workflows/${workflowId}/steps/${stepId}`), 'Failed to delete step');
}

// ---------------------------------------------------------------------------
// Aggregate export
// ---------------------------------------------------------------------------

export const locationsDepartmentsAdmin = {
  // Locations
  getLocationLogoUrl,
  uploadLocationLogo,
  getLocations,
  getLocationsWithCounts,
  getAccountLocationsPage,
  getAccountLocationNames,
  searchLocations,
  searchLocationsPage,
  getLocation,
  getLocationDashboard,
  createLocation,
  updateLocation,
  deleteLocation,
  undeleteLocation,
  // Department sections
  listDepartmentSections,
  createDepartmentSection,
  updateDepartmentSection,
  moveDepartmentSectionUp,
  moveDepartmentSectionDown,
  reorderDepartmentSection,
  deleteDepartmentSection,
  undeleteDepartmentSection,
  removeDepartmentFromSection,
  // Departments
  getDepartments,
  getAccountDepartments,
  getMyDepartments,
  createDepartment,
  updateDepartment,
  batchReorderDepartments,
  moveDepartmentUp,
  moveDepartmentDown,
  deleteDepartment,
  undeleteDepartment,
  attachServiceToDepartment,
  detachServiceFromDepartment,
  // Workflows
  getLocationWorkflows,
  createWorkflow,
  updateWorkflow,
  deleteWorkflow,
  getWorkflowSteps,
  addWorkflowStep,
  moveWorkflowStep,
  deleteWorkflowStep,
};
